import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Role, SessionUser, LeagueSummaryDTO } from '@pickem/shared';
import { api } from './api';

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  leagues: LeagueSummaryDTO[];
  activeLeagueId: string | null;
  isLeagueStaff: boolean;
  login: (email: string, password: string) => Promise<SessionUser>;
  register: (input: RegisterInput) => Promise<SessionUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  switchLeague: (id: string) => Promise<void>;
}

export interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [leagues, setLeagues] = useState<LeagueSummaryDTO[]>([]);
  const [activeLeagueId, setActiveLeagueId] = useState<string | null>(null);

  async function refreshLeagues() {
    try {
      const res = await api.get<{ leagues: LeagueSummaryDTO[]; activeLeagueId: string | null }>(
        '/leagues',
      );
      setLeagues(res.leagues);
      setActiveLeagueId(res.activeLeagueId);
    } catch {
      setLeagues([]);
      setActiveLeagueId(null);
    }
  }

  async function refresh() {
    try {
      const res = await api.get<{ user: SessionUser | null }>('/auth/me');
      setUser(res.user);
      if (res.user) {
        await refreshLeagues();
      } else {
        setLeagues([]);
        setActiveLeagueId(null);
      }
    } catch {
      setUser(null);
      setLeagues([]);
      setActiveLeagueId(null);
    }
  }

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const login: AuthState['login'] = async (email, password) => {
    const res = await api.post<{ user: SessionUser }>('/auth/login', { email, password });
    setUser(res.user);
    await refreshLeagues();
    return res.user;
  };

  const register: AuthState['register'] = async (input) => {
    const res = await api.post<{ user: SessionUser }>('/auth/register', input);
    setUser(res.user);
    await refreshLeagues();
    return res.user;
  };

  const logout: AuthState['logout'] = async () => {
    await api.post('/auth/logout');
    setUser(null);
    setLeagues([]);
    setActiveLeagueId(null);
  };

  const switchLeague: AuthState['switchLeague'] = async (id) => {
    await api.post('/leagues/active', { leagueId: id });
    await refreshLeagues();
  };

  const isLeagueStaff = user
    ? user.isSuperAdmin || leagues.find((l) => l.id === activeLeagueId)?.role === ('STAFF' as Role)
    : false;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        leagues,
        activeLeagueId,
        isLeagueStaff,
        login,
        register,
        logout,
        refresh,
        switchLeague,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
